import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import { Horse } from '@phosphor-icons/react';
import { Bar, Doughnut } from 'react-chartjs-2';
import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import api from '../../services/api';
import { SMART_COLORS as COLORS, smartCardSx as cardSx } from '../../theme/smartStableTheme';

Chart.register(ChartDataLabels);

const CHART_COLORS = [
  COLORS.gold,
  COLORS.blue,
  COLORS.green,
  COLORS.rose,
  '#9a8f78',
  '#c7b783',
  '#8aa7a0',
];

const emptyStats = {
  horses: [],
  totalHorses: 0,
  ownersCount: 0,
  avgAge: 0,
  genderDistribution: {},
  colorDistribution: {},
  breedDistribution: {},
  countryDistribution: {},
  ownerHorseCount: {},
  stableDistribution: {},
  roomDistribution: {},
  totalRooms: 0,
  fullRooms: 0,
  availableRooms: 0,
  maintenanceRooms: 0,
  occupancyRate: 0,
};

function getAge(dob) {
  if (!dob) return null;
  const birth = new Date(dob);
  if (Number.isNaN(birth.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  if (
    today.getMonth() < birth.getMonth() ||
    (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate())
  ) {
    age -= 1;
  }
  return Math.max(age, 0);
}

function horseGender(horse) {
  const age = getAge(horse.date_of_birth);
  const gender = String(horse.gender || '').toUpperCase();
  if (gender === 'S') return age !== null && age < 3 ? 'COLT' : 'STALLION';
  if (gender === 'M') return age !== null && age < 3 ? 'FILLY' : 'MARE';
  return 'UNKNOWN';
}

function addCount(target, key, inc = 1) {
  if (!key) return;
  target[key] = (target[key] || 0) + inc;
}

function topEntries(distribution, limit = 6, otherLabel = 'Other') {
  const entries = Object.entries(distribution || {})
    .filter(([, value]) => Number(value || 0) > 0)
    .sort((a, b) => Number(b[1]) - Number(a[1]));

  const top = entries.slice(0, limit);
  const rest = entries.slice(limit).reduce((sum, [, value]) => sum + Number(value || 0), 0);
  return rest > 0 ? [...top, [otherLabel, rest]] : top;
}

function formatNumber(value) {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Number(value || 0));
}

function getLabel(item, language, arKey, enKey, fallbackKey) {
  return language === 'ar'
    ? item?.[arKey] || item?.[enKey] || item?.[fallbackKey]
    : item?.[enKey] || item?.[arKey] || item?.[fallbackKey];
}

function chartOptions({ horizontal = false } = {}) {
  return {
    indexAxis: horizontal ? 'y' : 'x',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: true },
      datalabels: {
        color: COLORS.text,
        anchor: 'end',
        align: 'end',
        clamp: true,
        font: { weight: '700', size: 11 },
        formatter: (value) => (Number(value) > 0 ? formatNumber(value) : ''),
      },
    },
    scales: {
      x: {
        grid: { color: '#eee7d8' },
        ticks: { color: COLORS.muted, precision: 0 },
      },
      y: {
        grid: { display: false },
        ticks: { color: COLORS.muted },
      },
    },
  };
}

function doughnutOptions() {
  return {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '62%',
    plugins: {
      legend: {
        position: 'bottom',
        labels: { boxWidth: 10, color: COLORS.text, usePointStyle: true },
      },
      datalabels: {
        color: '#fff',
        font: { weight: '800', size: 11 },
        formatter: (value) => (Number(value) > 0 ? formatNumber(value) : ''),
      },
    },
  };
}

function ResponsiveGrid({ children, min = 260, sx }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`,
        gap: 2,
        alignItems: 'stretch',
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function MetricCard({ icon, title, value, helper, color = COLORS.gold, soft = COLORS.goldSoft }) {
  return (
    <Paper elevation={0} sx={{ ...cardSx, p: 2.2, minHeight: 118 }}>
      <Stack direction="row" spacing={1.6} alignItems="center">
        <Avatar sx={{ bgcolor: soft, color, width: 48, height: 48 }}>{icon}</Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ color, fontWeight: 900 }}>{title}</Typography>
          <Typography variant="h5" sx={{ color: COLORS.text, fontWeight: 900, mt: 0.3 }}>
            {value}
          </Typography>
          {helper ? (
            <Typography variant="caption" sx={{ color: COLORS.muted }}>
              {helper}
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </Paper>
  );
}

function SectionCard({ title, subtitle, children, sx }) {
  return (
    <Paper elevation={0} sx={{ ...cardSx, p: 2, minHeight: 320, ...sx }}>
      <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900 }}>
        {title}
      </Typography>
      {subtitle ? (
        <Typography variant="caption" sx={{ color: COLORS.muted }}>
          {subtitle}
        </Typography>
      ) : null}
      <Box sx={{ mt: 1.5 }}>{children}</Box>
    </Paper>
  );
}

function DistributionList({ rows, total, emptyLabel }) {
  if (!rows.length) {
    return <Typography sx={{ color: COLORS.muted, py: 3 }}>{emptyLabel}</Typography>;
  }

  return (
    <Stack spacing={1.2}>
      {rows.map(([label, value], index) => {
        const percent = total > 0 ? (Number(value) / total) * 100 : 0;
        return (
          <Box key={`${label}-${index}`}>
            <Stack direction="row" justifyContent="space-between" gap={2}>
              <Typography noWrap sx={{ color: COLORS.text, fontWeight: 800 }}>
                {label}
              </Typography>
              <Typography sx={{ color: COLORS.text, fontWeight: 900, whiteSpace: 'nowrap' }}>
                {formatNumber(value)} <Typography component="span" variant="caption" sx={{ color: COLORS.muted }}>({percent.toFixed(1)}%)</Typography>
              </Typography>
            </Stack>
            <Box sx={{ height: 8, bgcolor: COLORS.cardSoft, borderRadius: 99, mt: 0.6 }}>
              <Box
                sx={{
                  height: '100%',
                  width: `${Math.max(4, percent)}%`,
                  bgcolor: CHART_COLORS[index % CHART_COLORS.length],
                  borderRadius: 99,
                }}
              />
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
}

export default function Statistics() {
  const { t, i18n } = useTranslation();
  const language = i18n.language === 'ar' ? 'ar' : 'en';
  const [stats, setStats] = useState(emptyStats);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadStats = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const [
        horsesResponse,
        ownersResponse,
        colorsResponse,
        breedsResponse,
        countriesResponse,
        accommodationsResponse,
      ] = await Promise.all([
        api.get('/horses'),
        api.get('/owners'),
        api.get('/horse_colors'),
        api.get('/horse_breeds'),
        api.get('/countries'),
        api.get('/accommodations/summary'),
      ]);

      const horses = Array.isArray(horsesResponse.data) ? horsesResponse.data : [];
      const owners = Array.isArray(ownersResponse.data) ? ownersResponse.data : [];
      const colors = Array.isArray(colorsResponse.data) ? colorsResponse.data : [];
      const breeds = Array.isArray(breedsResponse.data) ? breedsResponse.data : [];
      const countries = Array.isArray(countriesResponse.data) ? countriesResponse.data : [];
      const accommodation = accommodationsResponse.data || {};

      const colorsMap = new Map(
        colors.map((item) => [
          item.color_id,
          getLabel(item, language, 'color_arabic', 'color_english', 'color_name'),
        ])
      );
      const breedsMap = new Map(
        breeds.map((item) => [
          item.breed_id,
          getLabel(item, language, 'breed_arabic', 'breed_english', 'breed_name'),
        ])
      );
      const countriesMap = new Map(
        countries.map((item) => [
          item.country_id,
          getLabel(item, language, 'country_arabic', 'country_english', 'country_name'),
        ])
      );
      const ownersMap = new Map(
        owners.map((owner) => [
          owner.owner_id,
          [owner.first_name, owner.last_name].filter(Boolean).join(' ') || `#${owner.owner_id}`,
        ])
      );

      const genderDistribution = {};
      const ageDistribution = {};
      const colorDistribution = {};
      const breedDistribution = {};
      const countryDistribution = {};
      const ownerHorseCount = {};

      let totalAge = 0;
      let ageCount = 0;

      horses.forEach((horse) => {
        const age = getAge(horse.date_of_birth);
        if (age !== null) {
          totalAge += age;
          ageCount += 1;
          const ageGroup = age <= 3 ? '1-3' : age <= 8 ? '4-8' : age <= 12 ? '9-12' : '>12';
          addCount(ageDistribution, ageGroup);
        }

        addCount(genderDistribution, horseGender(horse));
        addCount(colorDistribution, colorsMap.get(horse.color_id));
        addCount(breedDistribution, breedsMap.get(horse.breed_id));
        addCount(countryDistribution, countriesMap.get(horse.country_of_origin_id));
        addCount(ownerHorseCount, ownersMap.get(horse.owner_id));
      });

      setStats({
        horses,
        totalHorses: horses.length,
        ownersCount: owners.length,
        avgAge: ageCount ? totalAge / ageCount : 0,
        genderDistribution,
        ageDistribution,
        colorDistribution,
        breedDistribution,
        countryDistribution,
        ownerHorseCount,
        roomDistribution: accommodation.byRoom || {},
        stableDistribution: accommodation.byStable || {},
        totalRooms: accommodation.totalRooms || 0,
        fullRooms: accommodation.fullRooms || 0,
        availableRooms: accommodation.availableRooms || 0,
        maintenanceRooms: accommodation.maintenanceRooms || 0,
        occupancyRate: accommodation.occupancyRate || 0,
      });
    } catch (err) {
      setError(t('error_fetching_data') || 'تعذر تحميل الإحصائيات');
    } finally {
      setLoading(false);
    }
  }, [language, t]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const genderRows = useMemo(
    () => {
      const labels = {
        STALLION: t('STALLION') || 'Stallion',
        MARE: t('MARE') || 'Mare',
        COLT: t('COLT') || 'Colt',
        FILLY: t('FILLY') || 'Filly',
        UNKNOWN: t('unknown') || 'Unknown',
      };

      return ['STALLION', 'MARE', 'COLT', 'FILLY', 'UNKNOWN']
        .map((key) => [labels[key], stats.genderDistribution[key] || 0])
        .filter(([, value]) => value > 0);
    },
    [stats.genderDistribution, t]
  );
  const topOwners = useMemo(
    () => topEntries(stats.ownerHorseCount, 8, t('others') || 'أخرى'),
    [stats.ownerHorseCount, t]
  );
  const topColors = useMemo(
    () => topEntries(stats.colorDistribution, 6, t('others') || 'أخرى'),
    [stats.colorDistribution, t]
  );
  const topBreeds = useMemo(
    () => topEntries(stats.breedDistribution, 8, t('others') || 'أخرى'),
    [stats.breedDistribution, t]
  );
  const topCountries = useMemo(
    () => topEntries(stats.countryDistribution, 8, t('others') || 'أخرى'),
    [stats.countryDistribution, t]
  );
  const topStables = useMemo(
    () => topEntries(stats.stableDistribution, 6, t('others') || 'أخرى'),
    [stats.stableDistribution, t]
  );

  const ageRows = ['1-3', '4-8', '9-12', '>12'].map((key) => [
    key,
    stats.ageDistribution?.[key] || 0,
  ]);

  if (loading) {
    return (
      <Box sx={{ minHeight: '60vh', display: 'grid', placeItems: 'center' }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <CircularProgress size={26} />
          <Typography>{t('loading') || 'Loading'}...</Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      dir={language === 'ar' ? 'rtl' : 'ltr'}
      sx={{ bgcolor: COLORS.page, minHeight: '100%', px: { xs: 2, md: 3 }, py: 3 }}
    >
      <Box sx={{ maxWidth: 1540, mx: 'auto' }}>
        <Paper
          elevation={0}
          sx={{
            ...cardSx,
            p: { xs: 2, md: 2.5 },
            mb: 2.5,
            background: 'linear-gradient(135deg, #fffdf8 0%, #f4efe5 100%)',
          }}
        >
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={2}>
            <Box>
              <Typography variant="h4" sx={{ color: COLORS.text, fontWeight: 900 }}>
                {t('Statistics') || 'Statistics'}
              </Typography>
              <Typography sx={{ color: COLORS.muted, mt: 0.5 }}>
                {language === 'ar'
                  ? 'ملخص قابل للقراءة لأهم مؤشرات الخيل والسكن والعملاء.'
                  : 'Readable overview of horses, accommodation, and owner concentration.'}
              </Typography>
            </Box>
            <Chip
              icon={<QueryStatsIcon />}
              label={language === 'ar' ? 'أعلى العناصر فقط لتقليل الازدحام' : 'Top items only for clarity'}
              sx={{ alignSelf: { xs: 'flex-start', md: 'center' }, bgcolor: COLORS.goldSoft, color: COLORS.text, fontWeight: 800 }}
            />
          </Stack>
        </Paper>

        {error ? <Alert severity="error" sx={{ mb: 2.5 }}>{error}</Alert> : null}

        <ResponsiveGrid min={220} sx={{ mb: 2.5 }}>
          <MetricCard icon={<Horse size={26} weight="fill" />} title={t('total_horses') || 'Total horses'} value={formatNumber(stats.totalHorses)} helper={t('currentData') || ''} />
          <MetricCard icon={<PeopleAltIcon />} title={t('owners') || 'Owners'} value={formatNumber(stats.ownersCount)} helper={language === 'ar' ? 'عدد العملاء المسجلين' : 'Registered owners'} color={COLORS.blue} soft={COLORS.blueSoft} />
          <MetricCard icon={<HomeWorkIcon />} title={t('occupancy_rate') || 'Occupancy rate'} value={`${formatNumber(stats.occupancyRate)}%`} helper={`${formatNumber(stats.fullRooms)} / ${formatNumber(stats.totalRooms)} ${t('rooms') || 'rooms'}`} color={COLORS.green} soft={COLORS.greenSoft} />
          <MetricCard icon={<HomeWorkIcon />} title={t('available_rooms') || 'Available rooms'} value={formatNumber(stats.availableRooms)} helper={`${formatNumber(stats.maintenanceRooms)} ${t('maintenance_rooms') || 'maintenance'}`} color={COLORS.rose} soft={COLORS.roseSoft} />
          <MetricCard icon={<QueryStatsIcon />} title={t('average_age') || 'Average age'} value={stats.avgAge.toFixed(1)} helper={language === 'ar' ? 'سنة' : 'years'} />
        </ResponsiveGrid>

        <ResponsiveGrid min={340} sx={{ mb: 2.5 }}>
          <SectionCard title={t('gender_distribution') || 'Gender distribution'}>
            <Box sx={{ height: 260 }}>
              <Doughnut
                data={{
                  labels: genderRows.map(([label]) => label),
                  datasets: [{ data: genderRows.map(([, value]) => value), backgroundColor: CHART_COLORS }],
                }}
                options={doughnutOptions()}
              />
            </Box>
          </SectionCard>

          <SectionCard title={language === 'ar' ? 'الفئات العمرية' : 'Age groups'}>
            <Box sx={{ height: 260 }}>
              <Bar
                data={{
                  labels: ageRows.map(([label]) => label),
                  datasets: [{ data: ageRows.map(([, value]) => value), backgroundColor: COLORS.blue }],
                }}
                options={chartOptions()}
              />
            </Box>
          </SectionCard>

          <SectionCard title={t('horses_per_owner') || 'Horses per owner'} subtitle={language === 'ar' ? 'أعلى 8 عملاء فقط' : 'Top 8 owners only'}>
            <DistributionList rows={topOwners} total={stats.totalHorses} emptyLabel={t('no_data') || 'No data'} />
          </SectionCard>
        </ResponsiveGrid>

        <ResponsiveGrid min={340} sx={{ mb: 2.5 }}>
          <SectionCard title={t('colors_distribution') || 'Colors distribution'}>
            <DistributionList rows={topColors} total={stats.totalHorses} emptyLabel={t('no_data') || 'No data'} />
          </SectionCard>

          <SectionCard title={t('breed_distribution') || 'Breed distribution'} subtitle={language === 'ar' ? 'الأكثر تكرارًا فقط' : 'Most frequent only'}>
            <Box sx={{ height: 300 }}>
              <Bar
                data={{
                  labels: topBreeds.map(([label]) => label),
                  datasets: [{ data: topBreeds.map(([, value]) => value), backgroundColor: COLORS.gold }],
                }}
                options={chartOptions({ horizontal: true })}
              />
            </Box>
          </SectionCard>

          <SectionCard title={t('country_of_origin') || 'Country of origin'} subtitle={language === 'ar' ? 'الأكثر تكرارًا فقط' : 'Most frequent only'}>
            <Box sx={{ height: 300 }}>
              <Bar
                data={{
                  labels: topCountries.map(([label]) => label),
                  datasets: [{ data: topCountries.map(([, value]) => value), backgroundColor: COLORS.green }],
                }}
                options={chartOptions({ horizontal: true })}
              />
            </Box>
          </SectionCard>
        </ResponsiveGrid>

        <ResponsiveGrid min={340}>
          <SectionCard title={language === 'ar' ? 'توزيع الإسطبلات' : 'Stable distribution'}>
            <DistributionList rows={topStables} total={stats.fullRooms || stats.totalHorses} emptyLabel={t('no_data') || 'No data'} />
          </SectionCard>

          <SectionCard title={language === 'ar' ? 'حالة الغرف' : 'Room status'} sx={{ gridColumn: { lg: 'span 2' } }}>
            <ResponsiveGrid min={170}>
              {[
                { label: t('occupied_rooms') || 'Occupied', value: stats.fullRooms, color: COLORS.green, soft: COLORS.greenSoft },
                { label: t('available_rooms') || 'Available', value: stats.availableRooms, color: COLORS.blue, soft: COLORS.blueSoft },
                { label: t('maintenance_rooms') || 'Maintenance', value: stats.maintenanceRooms, color: COLORS.rose, soft: COLORS.roseSoft },
                { label: t('total_rooms') || 'Total', value: stats.totalRooms, color: COLORS.gold, soft: COLORS.goldSoft },
              ].map((item) => (
                <Paper key={item.label} elevation={0} sx={{ border: `1px solid ${COLORS.line}`, borderRadius: 2, p: 1.6, bgcolor: item.soft }}>
                  <Typography sx={{ color: item.color, fontWeight: 900 }}>{item.label}</Typography>
                  <Typography variant="h5" sx={{ color: COLORS.text, fontWeight: 900 }}>{formatNumber(item.value)}</Typography>
                </Paper>
              ))}
            </ResponsiveGrid>
          </SectionCard>
        </ResponsiveGrid>
      </Box>
    </Box>
  );
}
